import { supabase } from '../integrations/supabase/client';
import { getAvailabilityForDate, canServiceFit } from './availability';
import { isTableMissingError } from './supabaseErrors';

/**
 * Generates a secure URL-safe random token for offer acceptance
 * @returns 43-character URL-safe token (32 bytes base64url-encoded)
 */
function generateAcceptanceToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export interface WaitlistSlotData {
  businessId: string;
  serviceId: string;
  availableDate: string;
  availableTime: string;
}

/**
 * Notifies waitlist entries when a slot becomes available due to cancellation
 */
export async function notifyWaitlistSlotAvailable(slotData: WaitlistSlotData): Promise<void> {
  try {
    console.log('Notifying waitlist about available slot:', slotData);

    // Check if waitlist_entries table exists (graceful degradation during rollout)
    const { error: tableCheckError } = await supabase
      .from('waitlist_entries')
      .select('id')
      .limit(0);

    if (isTableMissingError(tableCheckError)) {
      return;
    }

    // Find waitlist entries for this service and date
    // Join to clients table to get contact info for SMS notifications
    // Production schema uses 'name' column (not first_name/last_name)
    const { data: waitlistEntries, error: waitlistError } = await supabase
      .from('waitlist_entries')
      .select(`
        id,
        status,
        client_id,
        clients!inner(
          name,
          phone
        )
      `)
      .eq('business_id', slotData.businessId)
      .eq('service_id', slotData.serviceId)
      .eq('requested_date', slotData.availableDate)
      .eq('status', 'active')
      .order('created_at', { ascending: true }); // First come, first served

    if (waitlistError) {
      console.error('Error fetching waitlist entries:', waitlistError);
      return;
    }

    if (!waitlistEntries || waitlistEntries.length === 0) {
      console.log('No active waitlist entries found for this slot');
      return;
    }

    // Notify the first person on the waitlist (FIFO)
    const firstEntry = waitlistEntries[0];
    const clientName = firstEntry.clients.name;
    console.log(`Notifying first waitlist entry: ${clientName} (ID: ${firstEntry.id})`);

    // Enforce invariant: at most one pending offer per waitlist entry
    const { data: existingOffer, error: checkOfferError } = await supabase
      .from('waitlist_offers')
      .select('id')
      .eq('waitlist_entry_id', firstEntry.id)
      .eq('status', 'pending')
      .maybeSingle();

    if (checkOfferError) {
      console.error('Error checking existing offers:', checkOfferError);
      return;
    }

    if (existingOffer) {
      console.warn(`Waitlist entry ${firstEntry.id} already has a pending offer. Skipping offer creation.`);
      return;
    }

    // Validate availability before creating offer
    // Ensures slot actually fits service duration and business is open
    const availability = await getAvailabilityForDate(
      slotData.businessId,
      null, // v1: default provider (NULL)
      slotData.availableDate
    );

    // Fetch service duration
    const { data: service, error: serviceError } = await supabase
      .from('business_services')
      .select('duration')
      .eq('id', slotData.serviceId)
      .single();

    if (serviceError || !service) {
      console.error('Error fetching service:', serviceError);
      return;
    }

    // Validate time slot fits service duration
    if (!canServiceFit(availability, slotData.availableTime, service.duration)) {
      console.warn(
        `Cannot create offer: time ${slotData.availableTime} doesn't fit service duration ${service.duration}min on ${slotData.availableDate}`,
        { businessId: slotData.businessId, serviceId: slotData.serviceId, availability }
      );
      return; // Don't create offer for invalid slot
    }

    // Create a waitlist offer for this available slot
    const offerExpiresAt = new Date();
    offerExpiresAt.setHours(offerExpiresAt.getHours() + 2); // 2-hour window to respond

    // Generate secure acceptance token for SMS link
    const acceptanceToken = generateAcceptanceToken();

    const { data: createdOffer, error: offerError } = await supabase
      .from('waitlist_offers')
      .insert({
        waitlist_entry_id: firstEntry.id,
        offered_date: slotData.availableDate,
        offered_time_slot: slotData.availableTime.substring(0, 5), // Normalize HH:mm:ss to HH:mm
        acceptance_token: acceptanceToken,
        status: 'pending',
        expires_at: offerExpiresAt.toISOString()
      })
      .select('id')
      .single();

    if (offerError || !createdOffer) {
      console.error('Error creating waitlist offer:', offerError);
      return;
    }

    console.log(`Created waitlist offer ${createdOffer.id} for entry ${firstEntry.id}`);

    // Update waitlist entry status to 'notified'
    const { error: updateError } = await supabase
      .from('waitlist_entries')
      .update({ status: 'notified', updated_at: new Date().toISOString() })
      .eq('id', firstEntry.id);

    if (updateError) {
      console.error('Error updating waitlist entry status:', updateError);
      // Continue despite error - offer is created
    }

    // Trigger notification (fire-and-forget)
    const { error: notificationError } = await supabase.functions.invoke('waitlist-notifications', {
      body: {
        waitlist_offer_id: createdOffer.id
      }
    });

    if (notificationError) {
      console.error('Failed to send waitlist notification:', notificationError);
      // Don't throw - notification failure must not break cancellation flow
    } else {
      console.log(`Successfully triggered notification for offer ${createdOffer.id}`);
    }

  } catch (error) {
    console.error('Error in notifyWaitlistSlotAvailable:', error);
  }
}